import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Flex,
  Text,
  Box,
  Divider,
  Thead,
  Tr,
  Table,
  Th,
  Tbody,
  Td,
  Tooltip
} from '@chakra-ui/react';
import {
  Badge,
  Button,
  FilterSelect,
  Pagination,
  SearchInput
} from '@morressier/alembic';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { fetchSubmissionNotes } from 'src/api/queries';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import {
  INoteCategory,
  noteCategories
} from '@publishing-engine/domain/models';
import { useForm } from 'react-hook-form';
import { pluralizeString } from '../../../utils/string';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  faSearch,
  faEdit,
  faTrashAlt,
  faFile
} from '@fortawesome/pro-regular-svg-icons';
import SortMenu, {
  SortField
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';
import NoteContextMenu from '../NotesTab/NoteContextMenu';
import { useKeycloak } from 'src/components/AuthHOC';
import { ISubmissionNotesListItem } from '@publishing-engine/domain/views';
import ViewNoteModal from '../NotesTab/ViewNoteModal';
import { format } from 'date-fns';
import { NOTES_SORT_OPTIONS } from 'src/constants';

interface IProps {
  emptyState?: React.ReactNode;
  filter?: {
    category?: INoteCategory[];
  };
  hideFilterOptions?: boolean;
}

export interface INoteListForwardRef {
  refetchNotes: () => void;
}

type INoteFilter = {
  category: INoteCategory[];
  searchParams: string;
};

const NoteList = forwardRef<INoteListForwardRef, IProps>(
  ({ filter, emptyState, hideFilterOptions }, ref) => {
    const {
      fieldsResponse: { organizationId },
      submissionId
    } = useSubmissionData();
    const ctxId = useCurrentContextId() ?? '';
    const searchParams = new URLSearchParams(location.search);
    const limit = 15;
    const [page, setPage] = useState<number>(1);
    const [searchValue, setSearchValue] = useState('');
    const [sort, setSort] = useState<SortField | null>(null);
    const { currentUser } = useKeycloak();
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [inlineEditTrigger, setInlineEditTrigger] = useState<string | null>(
      null
    );
    const [inlineDeleteTrigger, setInlineDeleteTrigger] = useState<
      string | null
    >(null);

    const [selectedNote, setSelectedNote] =
      useState<ISubmissionNotesListItem | null>(null);
    const [typedValue, setTypedValue] = useState('');

    const noteId = searchParams.get('focusNoteId');
    const { watch, resetField, control, setValue } = useForm<INoteFilter>({
      defaultValues: filter ?? {
        category: []
      }
    });

    const category = watch('category');
    const searchText = watch('searchParams');

    const fetchNotes = (page = 1) => {
      const offset = (page - 1) * limit;
      const sortPayload: SortField = sort ?? {
        value: 'created_at',
        label: '',
        direction: 'desc'
      };
      const filtersPayload = searchValue?.trim()
        ? { searchParams: searchValue.trim() }
        : {};

      return ctxId && submissionId
        ? fetchSubmissionNotes({
            ctxId,
            submissionId,
            offset,
            limit,
            category,
            sort: sortPayload,
            filters: filtersPayload
          })
        : null;
    };

    const { data: notesData, refetch: refetchNotes } = useQuery({
      queryKey: [
        'submission-notes',
        {
          submissionId,
          page,
          limit,
          category,
          searchValue,
          sort
        }
      ],
      queryFn: () => fetchNotes(page),
      keepPreviousData: true
    });

    useEffect(() => {
      if (notesData && notesData.items.length === 0 && page > 1) {
        const lastValidPage = Math.ceil(notesData.total / limit) || 1;

        if (page !== lastValidPage) {
          setPage(lastValidPage);
        }
      }
    }, [notesData, page]);

    useEffect(() => {
      if (notesData?.total && noteId) {
        const element = document.getElementById(noteId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }, [notesData, noteId]);

    useEffect(() => {
      setPage(1);
    }, [category]);

    useEffect(() => {
      setPage(1);
    }, [sort]);

    useEffect(() => {
      if (inlineEditTrigger) setInlineEditTrigger(null);
      if (inlineDeleteTrigger) setInlineDeleteTrigger(null);
    }, [inlineEditTrigger, inlineDeleteTrigger]);

    const { total, items: notes } = notesData
      ? notesData
      : {
          total: 0,
          items: []
        };
    const offset = (page - 1) * limit;
    const totalPages = Math.ceil(total / limit);
    const startIndex = offset + 1;
    const endIndex = Math.min(offset + limit, total);

    const handleSortApply = (selected: SortField | null) => {
      setSort(selected);
      setPage(1);
    };

    const handleSortReset = () => {
      setSort(null);
      setPage(1);
    };

    const sortOptions = {
      fields: NOTES_SORT_OPTIONS
    };

    useImperativeHandle(
      ref,
      () => ({
        refetchNotes() {
          refetchNotes();
        }
      }),
      [refetchNotes]
    );

    return (
      <Flex flexDirection="column" marginTop={10}>
        {!hideFilterOptions && (
          <Flex alignItems={'center'} mb={6}>
            {total > 1 ? (
              <Text textStyle="headingItem" fontSize="xl">
                {total} {pluralizeString('Note', total)}
              </Text>
            ) : null}
          </Flex>
        )}
        <Flex flexDirection={'column'} gap={2}>
          <Flex gap={4} mb={6} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
            <Flex gap={2} alignItems="center" flex="1">
              <SearchInput
                placeholder="Search notes by title & content"
                iconLeft={<FontAwesomeIcon icon={faSearch} />}
                width="100%"
                name="searchParams"
                value={typedValue}
                enterKeyHint="search"
                onChange={e => setTypedValue(e.currentTarget.value)}
                onInputClear={() => {
                  setTypedValue('');
                  setSearchValue('');
                  setValue('searchParams', '');
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const finalValue = typedValue.trim();
                    setSearchValue(finalValue);
                    setValue('searchParams', finalValue);
                  }
                }}
                onKeyUp={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const finalValue = typedValue.trim();
                    setSearchValue(finalValue);
                    setValue('searchParams', finalValue);
                  }
                }}
              />
              <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
                <Button
                  variant="primary"
                  aria-label="Search"
                  onClick={() => {
                    const finalValue = typedValue.trim();
                    setSearchValue(finalValue);
                    setValue('searchParams', finalValue);
                  }}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </Button>
              </Box>
            </Flex>
            <Box marginLeft="auto">
              <FilterSelect
                name="category"
                data-testid="filter-note-category"
                control={control}
                itemLabel="Category"
                watch={watch}
                items={noteCategories.map(value => ({
                  value,
                  name: `${value} Notes`
                }))}
                clearSelection={() => resetField('category')}
                pluralLabel="categories"
              />
            </Box>
          </Flex>
        </Flex>

        {notes?.length === 0 ? (
          emptyState ?? (
            <Flex gap={8} flexDirection="column">
              <Box
                marginTop={2}
                borderTop="2px solid"
                borderColor="gray.200"
                backgroundColor="white"
              >
                <Flex gap={2} padding={3} flexDirection="column">
                  <Text textStyle="headingItem">
                    {category.length
                      ? 'No Notes Found'
                      : notes?.length === 0 && searchText?.length === 0
                      ? 'No Notes Added'
                      : 'No Notes Found'}
                  </Text>
                  <Text textStyle="textContext">
                    {category.length
                      ? `No notes matching your selected ${pluralizeString(
                          'category',
                          category.length,
                          'categories'
                        )}. Please adjust your filters and try again.`
                      : notes?.length === 0 && searchText?.length === 0
                      ? 'No notes yet. Notes will be displayed here once added by any team members.'
                      : 'No notes match your search or filters. Please adjust and try again.'}
                  </Text>
                </Flex>
              </Box>
            </Flex>
          )
        ) : (
          <Flex
            alignItems={'center'}
            gap={2}
            height={5}
            justifyContent={'space-between'}
            mb={6}
          >
            <Flex>
              <Text textStyle="textBody">
                Showing <strong>{startIndex}</strong> –{' '}
                <strong>{endIndex}</strong> of <strong>{total}</strong> Note
                {total === 1 ? '' : 's'}
              </Text>
            </Flex>
            <Divider orientation="horizontal" flex="1" borderColor="#D9D9D9" />
            <SortMenu
              appliedSort={sort}
              options={sortOptions}
              onApply={handleSortApply}
              onReset={handleSortReset}
            />
          </Flex>
        )}
        {notes.length === 0 ? null : (
          <Box width="100%">
            <Box overflowX="auto" width="100%">
              <Table
                width="100%"
                minWidth="620px"
                sx={{
                  tableLayout: 'fixed',
                  borderCollapse: 'separate',
                  borderSpacing: '0px',
                  '& th, & td': {
                    px: '8px !important'
                  }
                }}
              >
                <Thead>
                  <Tr
                    sx={{
                      '& th': {
                        textTransform: 'none',
                        color: 'black',
                        fontSize: '14px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                      }
                    }}
                  >
                    <Th width="45%" textStyle="headingItem" whiteSpace="nowrap">
                      <Text>Title & Note</Text>
                    </Th>
                    <Th width="25%" textStyle="headingItem" whiteSpace="nowrap">
                      <Text>Users</Text>
                    </Th>
                    <Th width="20%" textStyle="headingItem" whiteSpace="nowrap">
                      <Text>Last Updated On</Text>
                    </Th>
                    <Th width="10%" textStyle="headingItem" whiteSpace="nowrap">
                      <Text>Actions</Text>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {notes.map(note => (
                    <Tr key={note.id} background="white">
                      <Td
                        width="45%"
                        overflow="hidden"
                        cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                        verticalAlign="top"
                        onClick={() => {
                          setSelectedNote(note);
                          setIsViewModalOpen(true);
                        }}
                      >
                        <Flex
                          direction="column"
                          alignItems="flex-start"
                          justifyContent="flex-start"
                          minWidth={0}
                          textAlign="left"
                        >
                          <Tooltip
                            hasArrow
                            borderRadius={4}
                            placement="top"
                            label={'View Note'}
                          >
                            <Box width="100%">
                              <Flex align="center">
                                <Text
                                  textStyle="headingLine"
                                  color="#2043DF"
                                  _hover={{
                                    textDecoration: 'underline',
                                    color: '#142480'
                                  }}
                                >
                                  {note.title?.trim() || 'Untitled'}
                                </Text>
                              </Flex>
                            </Box>
                          </Tooltip>
                          {note.title === 'Untitled' && (
                            <Box noOfLines={2} cursor="pointer">
                              <Text textStyle="textBody">{note?.content}</Text>
                            </Box>
                          )}
                          <Flex alignItems="center" gap={2} mt={1}>
                            {note?.attachments &&
                              note?.attachments?.length > 0 && (
                                <Flex alignItems="center" gap={1}>
                                  <FontAwesomeIcon
                                    icon={faFile}
                                    size="sm"
                                    color="gray.700"
                                  />
                                  <Text
                                    textStyle="textBody"
                                    color="gray.700"
                                    noOfLines={1}
                                  >
                                    {note.attachments.length}{' '}
                                    {note.attachments.length > 1
                                      ? 'Files'
                                      : 'File'}
                                  </Text>
                                </Flex>
                              )}

                            {note?.category && (
                              <Badge variant="neutral" size="context">
                                {note.category}
                              </Badge>
                            )}
                          </Flex>
                        </Flex>
                      </Td>
                      <Td width="25%">
                        <Text textStyle="headingLine">
                          {`${note?.addedBy?.firstName ?? ''} ${
                            note?.addedBy?.lastName ?? ''
                          }`.trim()}
                        </Text>
                        {note?.mentions && note?.mentions?.length > 0 && (
                          <Text>
                            @{note?.mentions[0]?.name}
                            {note.mentions.length > 1 &&
                              `, +${note.mentions.length - 1}`}
                          </Text>
                        )}
                      </Td>
                      <Td width="20%">
                        {format(new Date(note?.modified_at), 'MMM dd, yyyy')}
                      </Td>

                      <Td width="10%">
                        <Flex justifyContent="flex-end">
                          {currentUser?.id === note?.userId && (
                            <Flex
                              gap={3}
                              justifyContent="flex-end"
                              alignItems="center"
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                cursor="pointer"
                                color="#2043DF"
                                onClick={() => setInlineEditTrigger(note.id)}
                              />
                              <FontAwesomeIcon
                                icon={faTrashAlt}
                                cursor="pointer"
                                onClick={() => setInlineDeleteTrigger(note.id)}
                              />
                              <NoteContextMenu
                                data={note}
                                submissionId={submissionId}
                                submissionContextId={ctxId}
                                organizationId={organizationId}
                                fetchNotesQuery={[
                                  'submission-notes',
                                  { submissionId, page }
                                ]}
                                externalEditTrigger={
                                  inlineEditTrigger === note.id
                                }
                                externalDeleteTrigger={
                                  inlineDeleteTrigger === note.id
                                }
                              />
                            </Flex>
                          )}
                        </Flex>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            {totalPages > 1 && (
              <Flex justifyContent="center" marginY={3}>
                <Pagination
                  ariaLabel="Notes pagination"
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </Flex>
            )}
          </Box>
        )}
        {selectedNote && (
          <ViewNoteModal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            data={selectedNote}
          />
        )}
      </Flex>
    );
  }
);

export default NoteList;
